import type { GraphConfig, GraphEdge, GraphNode } from "./types";

// The force simulation and layout seeding for the shared graph engine. Ported
// from the design handoff's velocity-Verlet sim: charge repulsion + link
// springs + cluster anchors + disc-and-label collision. One layout, clustered:
// hubs orbit the core, leaves fan around their hub. Positions live in refs
// and are written to the DOM imperatively by ForceGraph; React never owns them.

/** A config with every numeric field the sim reads resolved to a value. */
export interface ResolvedConfig extends GraphConfig {
  width: number;
  height: number;
  charge: number;
  linkDistance: number;
  collide: number;
  layout: "clustered";
  designWidth: number;
  xStretch: number;
}

/** Node with the private seed fields that seedPositions writes. */
type SeededNode = GraphNode & {
  _x0?: number;
  _y0?: number;
  _tx?: number;
  _ty?: number;
  /** Placed by the deterministic tree layout, so the simulation must not move it. */
  _pinned?: boolean;
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx: number | null;
  fy: number | null;
  r: number;
  w: number;
  lw: number;
  lb: number;
  tx: number | null;
  ty: number | null;
  core: boolean;
}

interface Link {
  s: number;
  t: number;
  dist: number;
  k: number;
}

export interface Sim {
  P: Particle[];
  links: Link[];
  idIndex: Map<string, number>;
  step: () => number;
  reheat: (alpha?: number) => void;
  readonly alpha: number;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Get (creating if absent) the array bucket for a key. */
function bucket<T>(map: Record<string, T[]>, key: string): T[] {
  const existing = map[key];
  if (existing) return existing;
  const next: T[] = [];
  map[key] = next;
  return next;
}

/** How far a value chip may push a node past the size its weight earned. */
const MONO_MAX_GROWTH = 10;

export function nodeRadius(n: Pick<GraphNode, "kind" | "weight" | "mono">): number {
  if (n.kind === "core") return 46;
  if (n.kind === "hub") return 34;
  if (n.kind === "subhub") return 26;
  if (n.kind === "add") return 18;
  const w = n.weight || 1;
  const base = 20 + w * 6;
  if (!n.mono) return base;
  // The value chip sits on a chord at 0.44r inside the disc (11px mono, ~6.6px
  // per char). Grow the disc so the chord clears the text plus padding, instead
  // of letting a wide reading ("Moderate", "180ml") touch the ring. Chord width
  // at that height is ~1.8r.
  const monoR = Math.ceil((6.6 * n.mono.length + 10) / 1.8);
  // Capped, because on this map size reads as importance: the legend tells the
  // reader the rings mean durability. Letting the chip set the radius outright
  // meant a node saying "893 steps fewer" drew five times the area of one
  // saying "Quiet hours" on nothing but character count.
  return Math.min(Math.max(base, monoR), base + MONO_MAX_GROWTH);
}

export function makeSim(nodes: GraphNode[], edges: GraphEdge[], cfg: ResolvedConfig): Sim {
  const N = nodes.length;
  const idIndex = new Map(nodes.map((n, i) => [n.id, i]));
  const links: Link[] = edges
    .map((e) => ({
      s: idIndex.get(e.source),
      t: idIndex.get(e.target),
      dist: e.distance || cfg.linkDistance,
      k: e.strength ?? 0.5,
    }))
    .filter((l): l is Link => l.s != null && l.t != null);

  const P: Particle[] = (nodes as SeededNode[]).map((n) => {
    const r = nodeRadius(n);
    // Label footprint below the node, so the collision pass keeps text off
    // neighbours. Width estimated from label length at the label font size.
    const label = n.label || "";
    const charW = n.kind === "core" ? 8.2 : 6.7;
    const lw = Math.max(r, (label.length * charW) / 2 + 6);
    const lineH = 17;
    const lines = n.mono ? 2 : 1;
    const lb = r + 14 + lineH * lines;
    return {
      x: n._x0 || 0,
      y: n._y0 || 0,
      vx: 0,
      vy: 0,
      // A node the tree layout placed stays exactly where it was placed.
      fx: (n as SeededNode)._pinned ? ((n as SeededNode)._x0 ?? null) : null,
      fy: (n as SeededNode)._pinned ? ((n as SeededNode)._y0 ?? null) : null,
      r,
      w: n.weight || 1,
      lw,
      lb,
      tx: n._tx == null ? null : n._tx,
      ty: n._ty == null ? null : n._ty,
      core: n.kind === "core",
    };
  });
  // Every index in the kernels below is in-bounds by loop construction (i,j < N)
  // or derived from idIndex; this accessor keeps the hot paths free of scattered
  // non-null assertions under noUncheckedIndexedAccess.
  const idx = (k: number): Particle => P[k] as Particle;

  let alpha = 0;
  const alphaMin = 0.0;
  const alphaDecay = 0.02;
  const velDecay = 0.82;
  const cx = cfg.width / 2;
  const cy = cfg.height / 2;
  // Anchor relaxation: resolve disc + label-box overlaps in the anchors once at
  // setup, so the rest state is overlap-free and runtime forces never fight it.
  {
    const pad0 = cfg.collide;
    const lpad0 = cfg.labelPad != null ? cfg.labelPad : 6;
    for (let it = 0; it < 40; it++) {
      let moved = 0;
      for (let i = 0; i < N; i++) {
        const a = idx(i);
        for (let j = i + 1; j < N; j++) {
          const b = idx(j);
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const min = a.r + b.r + pad0;
          const d = Math.hypot(dx, dy) || 1;
          if (d < min) {
            const push = (min - d) / d;
            const wA = a.core ? 0 : b.core ? 1 : 0.5;
            const wB = a.core ? 1 : b.core ? 0 : 0.5;
            a.x -= dx * push * wA;
            a.y -= dy * push * wA;
            b.x += dx * push * wB;
            b.y += dy * push * wB;
            moved++;
          }
          const ovX = a.lw + b.lw + lpad0 - Math.abs(a.x - b.x);
          if (ovX <= 0) continue;
          const aT = a.y - a.r;
          const aB = a.y + a.lb;
          const bT = b.y - b.r;
          const bB = b.y + b.lb;
          const ovY = Math.min(aB, bB) - Math.max(aT, bT) + lpad0;
          if (ovY <= 0) continue;
          const wA = a.core ? 0 : b.core ? 1 : 0.5;
          const wB = a.core ? 1 : b.core ? 0 : 0.5;
          if (ovX < ovY) {
            const dir = a.x < b.x ? -1 : 1;
            a.x += dir * ovX * wA;
            b.x -= dir * ovX * wB;
          } else {
            const dir = a.y < b.y ? -1 : 1;
            a.y += dir * ovY * wA;
            b.y -= dir * ovY * wB;
          }
          moved++;
        }
      }
      if (!moved) break;
    }
    for (const p of P) {
      if (!p.core && p.tx != null) {
        p.tx = p.x;
        p.ty = p.y;
      }
    }
  }

  function step(): number {
    if (alpha > alphaMin) alpha = Math.max(alphaMin, alpha - alphaDecay * (alpha + 0.02));

    // charge repulsion (warmup only: x alpha, so it fades to nothing at rest)
    const charge = cfg.charge;
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        let dx = idx(j).x - idx(i).x;
        let dy = idx(j).y - idx(i).y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) {
          d2 = 1;
          dx = Math.random() - 0.5;
          dy = Math.random() - 0.5;
        }
        const d = Math.sqrt(d2);
        const f = ((charge * idx(i).w * idx(j).w) / d2) * alpha;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        idx(i).vx -= fx;
        idx(i).vy -= fy;
        idx(j).vx += fx;
        idx(j).vy += fy;
      }
    }

    // link springs (warmup only; anchors hold the rest shape)
    const linkScale = alpha;
    for (const l of links) {
      const a = idx(l.s);
      const b = idx(l.t);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.hypot(dx, dy) || 1;
      const diff = ((d - l.dist) / d) * linkScale * l.k * 0.5;
      const fx = dx * diff;
      const fy = dy * diff;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // anchor pull toward seeded targets; the core is held at centre
    {
      const kA = 0.055;
      for (let i = 0; i < N; i++) {
        const p = idx(i);
        if (p.fx != null) continue;
        if (p.core) {
          p.vx += (cx - p.x) * 0.18;
          p.vy += (cy - p.y) * 0.18;
          continue;
        }
        if (p.tx != null && p.ty != null) {
          p.vx += (p.tx - p.x) * kA;
          p.vy += (p.ty - p.y) * kA;
        }
      }
    }

    // collision: disc separation, then label-box separation
    const pad = cfg.collide;
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        const dx = idx(j).x - idx(i).x;
        const dy = idx(j).y - idx(i).y;
        const min = idx(i).r + idx(j).r + pad;
        const d = Math.hypot(dx, dy) || 1;
        if (d < min) {
          const push = ((min - d) / d) * 0.35;
          const px = dx * push;
          const py = dy * push;
          idx(i).x -= px;
          idx(i).y -= py;
          idx(j).x += px;
          idx(j).y += py;
        }
      }
    }
    const lpad = cfg.labelPad != null ? cfg.labelPad : 6;
    for (let it = 0; it < 3; it++) {
      for (let i = 0; i < N; i++) {
        const a = idx(i);
        for (let j = i + 1; j < N; j++) {
          const b = idx(j);
          const ovX = a.lw + b.lw + lpad - Math.abs(a.x - b.x);
          if (ovX <= 0) continue;
          const aT = a.y - a.r;
          const aB = a.y + a.lb;
          const bT = b.y - b.r;
          const bB = b.y + b.lb;
          const ovY = Math.min(aB, bB) - Math.max(aT, bT) + lpad;
          if (ovY <= 0) continue;
          if (ovX < ovY) {
            const s = (a.x < b.x ? -1 : 1) * ovX * 0.5;
            a.x += s;
            b.x -= s;
          } else {
            const s = (a.y < b.y ? -1 : 1) * ovY * 0.5;
            a.y += s;
            b.y -= s;
          }
        }
      }
    }

    // integrate
    let maxV = 0;
    for (let i = 0; i < N; i++) {
      const p = idx(i);
      if (p.fx != null) {
        p.x = p.fx;
        p.vx = 0;
      }
      if (p.fy != null) {
        p.y = p.fy;
        p.vy = 0;
      }
      p.vx *= velDecay;
      p.vy *= velDecay;
      if (Math.abs(p.vx) < 0.02) p.vx = 0;
      if (Math.abs(p.vy) < 0.02) p.vy = 0;
      p.vx = clamp(p.vx, -22, 22);
      p.vy = clamp(p.vy, -22, 22);
      p.x += p.vx;
      p.y += p.vy;
      p.x = clamp(p.x, p.r + 8, cfg.width - p.r - 8);
      p.y = clamp(p.y, p.r + 8, cfg.height - p.r - 8);
      const v = Math.abs(p.vx) + Math.abs(p.vy);
      if (v > maxV) maxV = v;
    }
    if (alpha < 0.05 && maxV < 0.05) alpha = 0;

    return alpha;
  }

  return {
    P,
    links,
    idIndex,
    step,
    reheat(a = 0.7) {
      alpha = Math.max(alpha, a);
    },
    get alpha() {
      return alpha;
    },
  };
}

const TAU = Math.PI * 2;

/** Seed initial node positions (clustered layout). Mutates _x0/_y0/_tx/_ty on nodes. */
/*
 * Deterministic radial cluster, the d3.cluster idea by hand.
 *
 * These graphs are shallow trees: a core, a few hubs, sometimes a level of
 * sub-hubs, then leaves. A force simulation is for topology you do not know,
 * and using one here bought nothing while costing everything that matters:
 * positions that differ run to run, no stable place to hang a label, and a
 * layout that keeps moving because the separation passes never stop pushing.
 *
 * Every leaf takes an equal slice of the whole circle and every parent sits at
 * the mean angle of its children. That is what keeps a crowded branch from
 * piling up: with twelve leaves the gap between neighbours is thirty degrees
 * whether they all hang off one hub or off four, where a fixed wedge per hub
 * squeezed thirteen descendants into a third of the circle and left the rest
 * of the canvas empty.
 */
function radialCluster(
  nodes: SeededNode[],
  edges: GraphEdge[],
  cfg: ResolvedConfig,
): Map<string, { x: number; y: number }> | null {
  const core = nodes.find((n) => n.kind === "core");
  if (!core) return null;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    (adj.get(e.source) ?? adj.set(e.source, []).get(e.source) ?? []).push(e.target);
    (adj.get(e.target) ?? adj.set(e.target, []).get(e.target) ?? []).push(e.source);
  }

  // Breadth-first from the core, so each node gets one parent and a depth.
  const depth = new Map<string, number>([[core.id, 0]]);
  const kids = new Map<string, string[]>();
  const queue = [core.id];
  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined) continue;
    for (const next of adj.get(id) ?? []) {
      if (depth.has(next)) continue;
      depth.set(next, (depth.get(id) ?? 0) + 1);
      (kids.get(id) ?? kids.set(id, []).get(id) ?? []).push(next);
      queue.push(next);
    }
  }

  /*
   * Hubs sit on the cardinals, and each owns the wedge around its own.
   *
   * Anchoring a hub to the mean angle of its children let the hubs drift into
   * each other and put an empty one on top of the core. Fixing them at north,
   * east, south and west is what makes the sibling map readable, and it holds
   * whatever the member happens to have in each tier.
   *
   * The wedge boundaries are not the midpoints, though. The gap between two
   * cardinals is divided in proportion to how much each side has to show, so a
   * tier holding ten memories borrows room from the one holding none, and its
   * leaves stay far enough apart to label. Each hub keeps its cardinal
   * regardless, so the shape of the map never moves.
   */
  const hubs = kids.get(core.id) ?? [];
  const leafCount = (id: string): number => {
    const children = kids.get(id) ?? [];
    if (children.length === 0) return 1;
    return children.reduce((sum, c) => sum + leafCount(c), 0);
  };

  const angle = new Map<string, number>();
  angle.set(core.id, 0);
  /** How much of the circle each parent was given, for sizing its ring. */
  const wedge = new Map<string, number>([[core.id, TAU]]);

  const cardinals = hubs.map((_, i) => -Math.PI / 2 + (i / Math.max(1, hubs.length)) * TAU);
  const weights = hubs.map((h) => leafCount(h));

  // Spread a node's children across the wedge it was given, then recurse.
  const spread = (id: string, lo: number, hi: number): void => {
    const children = kids.get(id) ?? [];
    if (children.length === 0) return;
    const total = children.reduce((sum, c) => sum + leafCount(c), 0) || children.length;
    let cursor = lo;
    for (const c of children) {
      const width = ((hi - lo) * leafCount(c)) / total;
      angle.set(c, cursor + width / 2);
      wedge.set(c, width);
      spread(c, cursor, cursor + width);
      cursor += width;
    }
  };

  hubs.forEach((hub, i) => {
    const here = cardinals[i] ?? 0;
    const wPrev = weights[(i - 1 + hubs.length) % hubs.length] ?? 1;
    const wHere = weights[i] ?? 1;
    const wNext = weights[(i + 1) % hubs.length] ?? 1;
    // Split each neighbouring gap in proportion to what the two sides hold.
    const gap = TAU / Math.max(1, hubs.length);
    const lo = here - (gap * wHere) / (wHere + wPrev);
    const hi = here + (gap * wHere) / (wHere + wNext);
    angle.set(hub, here);
    wedge.set(hub, hi - lo);
    // A hair inside the boundary, so two neighbours' outermost leaves do not
    // meet exactly on the line between their wedges.
    spread(hub, lo + 0.06, hi - 0.06);
  });

  const maxDepth = Math.max(1, ...[...depth.values()]);

  /*
   * Ring radii come from what the labels need, not from what the canvas has.
   *
   * Clamping the rings to the viewport meant the angular gap shrank as a branch
   * filled up while the radius stayed put, so ten memories under one hub landed
   * on top of each other. And because these positions are pinned, the collision
   * pass cannot rescue them: an overlap the layout creates is permanent. The
   * arc between two siblings has to be at least as wide as a label from the
   * start.
   *
   * The view fits itself afterwards, so a map that needs more room than the
   * canvas simply zooms out rather than piling up.
   */
  const CORE_CLEARANCE = 132;
  // A wrapped label is at most LABEL_LINE_CHARS wide; this is that, in pixels,
  // plus the gap that keeps two of them from touching.
  const LABEL_ARC = 128;
  const RING_GAP = 118;

  // The tightest ring is set by whichever parent has to fit the most children
  // into the wedge it was given.
  const ringRadius: number[] = [0];
  for (let d = 1; d <= maxDepth; d++) {
    let need = 0;
    for (const [parentId, children] of kids) {
      if ((depth.get(parentId) ?? -1) !== d - 1 || children.length === 0) continue;
      const spanA = Math.abs((wedge.get(parentId) ?? TAU) || TAU);
      const perChild = spanA / children.length;
      need = Math.max(need, LABEL_ARC / Math.max(0.08, perChild));
    }
    const previous = ringRadius[d - 1] ?? 0;
    ringRadius[d] = Math.max(need, previous + RING_GAP, d === 1 ? CORE_CLEARANCE : 0);
  }

  const cx = cfg.width / 2;
  const cy = cfg.height / 2;

  const out = new Map<string, { x: number; y: number }>();
  for (const n of nodes) {
    const d = depth.get(n.id);
    const a = angle.get(n.id);
    if (d === undefined || a === undefined) continue;
    const r = ringRadius[d] ?? 0;
    out.set(n.id, { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return out;
}

export function seedPositions(nodes: GraphNode[], cfg: ResolvedConfig, edges: GraphEdge[]): void {
  /*
   * The tree layout is the answer wherever the graph is a tree, which is every
   * graph this component draws. Positions are pinned, so the simulation has
   * nothing left to move and the map is identical on every load.
   */
  const placed = radialCluster(nodes as SeededNode[], edges, cfg);
  if (placed) {
    for (const n of nodes as SeededNode[]) {
      const at = placed.get(n.id);
      if (!at) continue;
      n._x0 = at.x;
      n._y0 = at.y;
      n._tx = at.x;
      n._ty = at.y;
      n._pinned = true;
    }
    return;
  }

  const seeded = nodes as SeededNode[];
  const cx = cfg.width / 2;
  const cy = cfg.height / 2;

  {
    const core = seeded.find((n) => n.kind === "core");
    if (core) {
      core._tx = cx;
      core._ty = cy;
      core._x0 = cx;
      core._y0 = cy;
    }
    const coreCluster = core ? core.cluster : null;
    const others = seeded.filter((n) => n !== core);

    const clusterOrder = cfg.clusters
      ? Object.keys(cfg.clusters)
      : [...new Set(others.map((n) => n.cluster))];
    const byC: Record<string, SeededNode[]> = {};
    others.forEach((n) => {
      bucket(byC, n.cluster).push(n);
    });
    let orbit = clusterOrder.filter((c) => byC[c]?.length && c !== coreCluster);
    const Rc = cfg.clusterR || 360;
    const Rleaf = cfg.leafR || 132;

    const clusterOf: Record<string, string> = {};
    seeded.forEach((n) => {
      clusterOf[n.id] = n.cluster;
    });
    const xlinks = (edges || []).filter((l) => {
      const a = clusterOf[l.source];
      const b = clusterOf[l.target];
      return a && b && a !== b;
    });
    const aff: Record<string, number> = {};
    xlinks.forEach((l) => {
      const a = clusterOf[l.source];
      const b = clusterOf[l.target];
      if (a === undefined || b === undefined) return;
      const k = a < b ? `${a}|${b}` : `${b}|${a}`;
      aff[k] = (aff[k] || 0) + (l.strength || 0.5);
    });
    const affOf = (a: string, b: string) => aff[a < b ? `${a}|${b}` : `${b}|${a}`] || 0;
    if (orbit.length > 2 && !cfg.symmetric) {
      const totalAff = (c: string) => orbit.reduce((s, o) => s + (o === c ? 0 : affOf(c, o)), 0);
      const first = orbit.slice().sort((a, b) => totalAff(b) - totalAff(a))[0];
      if (first !== undefined) {
        const chain = [first];
        const rest = orbit.filter((c) => c !== first);
        while (rest.length) {
          const tail = chain[chain.length - 1];
          if (tail === undefined) break;
          rest.sort((a, b) => affOf(tail, b) - affOf(tail, a));
          const next = rest.shift();
          if (next) chain.push(next);
        }
        orbit = chain;
      }
    }

    const sym = !!cfg.symmetric;
    const xf = cfg.xStretch || 1;
    const place = (n: SeededNode, x: number, y: number) => {
      const sx = cx + (x - cx) * xf;
      n._tx = sx;
      n._ty = y;
      n._x0 = sx;
      n._y0 = y;
    };

    const angOf: Record<string, number> = {};
    orbit.forEach((c, ci) => {
      const pin = cfg.clusters?.[c]?.angle;
      angOf[c] = pin != null ? (pin * Math.PI) / 180 : -Math.PI / 2 + (ci / orbit.length) * TAU;
    });

    const normA = (a: number) => {
      let x = a;
      while (x > Math.PI) x -= TAU;
      while (x < -Math.PI) x += TAU;
      return x;
    };
    orbit.forEach((c) => {
      const ang = angOf[c];
      const arr = byC[c];
      if (ang === undefined || !arr) return;
      const ccx = cx + Math.cos(ang) * Rc;
      const ccy = cy + Math.sin(ang) * Rc;
      const hub = arr.find((n) => n.kind === "hub");
      const subhubs = arr.filter((n) => n.kind === "subhub");
      const leaves = arr.filter((n) => n.kind !== "hub" && n.kind !== "subhub" && n.kind !== "add");
      const adders = arr.filter((n) => n.kind === "add");
      if (hub) place(hub, ccx, ccy);
      const keyOf = (n: SeededNode) => {
        const partners = xlinks
          .filter((l) => l.source === n.id || l.target === n.id)
          .map((l) => (l.source === n.id ? l.target : l.source))
          .map((id) => {
            const c2 = clusterOf[id];
            return c2 ? angOf[c2] : undefined;
          })
          .filter((a): a is number => a != null);
        if (!partners.length) return 0;
        return partners.reduce((s, a) => s + normA(a - ang), 0) / partners.length;
      };
      if (sym) {
        // Keep the balanced radial shape, but when a cluster's leaves link to
        // other clusters, order them by closeness so each leans toward the
        // neighbour it relates to. The fan is still symmetric around the axis;
        // only the ordering within it carries the cross-cluster affinity. With
        // no cross-links, fall back to the weight-balanced centre-out fan.
        const hasXlink = leaves.some((n) =>
          xlinks.some((l) => l.source === n.id || l.target === n.id),
        );
        if (hasXlink) {
          leaves.sort((a, b) => keyOf(a) - keyOf(b));
        } else {
          const byW = leaves.slice().sort((a, b) => (b.weight || 1) - (a.weight || 1));
          const mid: SeededNode[] = [];
          byW.forEach((n, i) => {
            if (i % 2) mid.push(n);
            else mid.unshift(n);
          });
          leaves.length = 0;
          leaves.push(...mid);
        }
      } else {
        leaves.sort((a, b) => keyOf(a) - keyOf(b));
      }
      /*
       * With sub-hubs the cluster is two rings, not one: each sub-hub takes a
       * slice of the fan, sits partway out on it, and its own leaves fan around
       * it. Without this a second hub in a cluster was never placed at all, so
       * it kept its seed position and dragged its edges across the canvas.
       */
      if (subhubs.length > 0) {
        const parentOf = new Map<string, string>();
        for (const l of edges || []) {
          if (subhubs.some((h) => h.id === l.source)) parentOf.set(l.target, l.source);
        }
        const sn = subhubs.length;
        const sSpread = sn <= 1 ? 0 : Math.min(Math.PI * 1.1, 0.5 + sn * 0.34);
        subhubs.forEach((sh, i) => {
          const t = sn === 1 ? 0.5 : i / (sn - 1);
          const sa = ang + (t - 0.5) * sSpread;
          const sx = ccx + Math.cos(sa) * (Rleaf * 0.62);
          const sy = ccy + Math.sin(sa) * (Rleaf * 0.62);
          place(sh, sx, sy);
          const kids = leaves.filter((l) => parentOf.get(l.id) === sh.id);
          const kn = kids.length;
          const kSpread = kn <= 1 ? 0 : Math.min(Math.PI * 0.92, 0.4 + kn * 0.3);
          kids.forEach((kid, j) => {
            const kt = kn === 1 ? 0.5 : j / (kn - 1);
            const ka = sa + (kt - 0.5) * kSpread;
            place(kid, sx + Math.cos(ka) * (Rleaf * 0.78), sy + Math.sin(ka) * (Rleaf * 0.78));
          });
        });
        // Anything the sub-hubs did not claim still belongs to the hub's own fan.
        const orphans = leaves.filter((l) => !parentOf.has(l.id));
        orphans.forEach((leaf, i) => {
          const t = orphans.length === 1 ? 0.5 : i / (orphans.length - 1);
          const la = ang + (t - 0.5) * 0.8;
          place(leaf, ccx + Math.cos(la) * Rleaf, ccy + Math.sin(la) * Rleaf);
        });
        adders.forEach((a) => {
          place(a, ccx + Math.cos(ang) * (Rleaf + 104), ccy + Math.sin(ang) * (Rleaf + 104));
        });
        return;
      }

      const n = leaves.length;
      const spread = n <= 1 ? 0 : Math.min(Math.PI * 1.24, 0.42 + n * 0.3);
      const stagger = n >= 5 && !sym;
      leaves.forEach((leaf, i) => {
        const t = n === 1 ? 0.5 : i / (n - 1);
        const la = ang + (t - 0.5) * spread;
        const R = Rleaf + (stagger && i % 2 ? 30 : 0);
        place(leaf, ccx + Math.cos(la) * R, ccy + Math.sin(la) * R);
      });
      adders.forEach((a) => {
        place(a, ccx + Math.cos(ang) * (Rleaf + 104), ccy + Math.sin(ang) * (Rleaf + 104));
      });
    });

    const coreKin = (byC[coreCluster ?? ""] || []).filter((n) => n !== core);
    const nodeAng = (n: SeededNode) => Math.atan2((n._ty ?? cy) - cy, (n._tx ?? cx) - cx);
    const byId: Record<string, SeededNode> = {};
    seeded.forEach((n) => {
      byId[n.id] = n;
    });
    const desired = coreKin.map((n, i) => {
      if (n.angle != null) return { n, a: (n.angle * Math.PI) / 180, fixed: true };
      const partners = (edges || [])
        .filter((l) => l.source === n.id || l.target === n.id)
        .map((l) => byId[l.source === n.id ? l.target : l.source])
        .filter((p): p is SeededNode => !!p && p !== core && p._tx != null);
      if (!partners.length) return { n, a: -Math.PI / 2 + (i / Math.max(1, coreKin.length)) * TAU };
      const sxy = partners.reduce<[number, number]>(
        (s, p) => {
          const a = nodeAng(p);
          return [s[0] + Math.cos(a), s[1] + Math.sin(a)];
        },
        [0, 0],
      );
      return { n, a: Math.atan2(sxy[1], sxy[0]) };
    });
    desired.sort((p, q) => p.a - q.a);
    const MINSEP = 0.7;
    for (let i = 1; i < desired.length; i++) {
      const cur = desired[i];
      const prev = desired[i - 1];
      if (!cur || !prev) continue;
      if (!cur.fixed && !prev.fixed && cur.a - prev.a < MINSEP) {
        cur.a = prev.a + MINSEP;
      }
    }
    desired.forEach(({ n, a }) => {
      const R = cfg.coreKinR || 128;
      place(n, cx + Math.cos(a) * R, cy + Math.sin(a) * R);
    });
    return;
  }
}
